<template>
  <b-container class="mt-5">
    <b-row class="justify-content-center">
      <b-col cols="12" md="8" lg="6">
        <b-button
          v-if="showTable"
          variant="primary"
          class="mb-3"
          block
          @click="back"
        >
          Back
        </b-button>
      </b-col>
    </b-row>

    <b-row class="justify-content-center">
      <b-col cols="12" md="8" lg="6" v-if="!showTable">
        <b-card header="Select Payroll Periods" header-tag="h4" class="shadow">
          <b-form>
            <!-- From Month -->
            <b-form-group label="Period 1" label-for="from-month-selector">
              <b-form-select
                id="from-month-selector"
                v-model="fromMonth"
                :options="last12Months"
                placeholder="Select from month"
              >
              </b-form-select>
            </b-form-group>

            <!-- To Month -->
            <b-form-group label="Period 2" label-for="to-month-selector">
              <b-form-select
                id="to-month-selector"
                v-model="toMonth"
                :options="last12Months"
                placeholder="Select to month"
              >
              </b-form-select>
            </b-form-group>

            <!-- Submit Button -->
            <b-button variant="primary" class="mt-3" block @click="submitRange">
              Submit
            </b-button>
          </b-form>
        </b-card>
      </b-col>

      <b-col cols="12" v-if="showTable">
        <b-card :header="tdsHeader" header-tag="h4" class="shadow">
          <!-- Export to CSV Button -->
          <b-button variant="success" class="mb-3" @click="exportToCSV">
            Export to CSV
          </b-button>

          <!-- Table -->
          <b-table
            striped
            hover
            responsive="sm"
            :items="formattedTableData"
            :fields="tableFields"
          >
          </b-table>
        </b-card>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import Swal from "sweetalert2";
import {
  BContainer,
  BRow,
  BCol,
  BCard,
  BForm,
  BFormGroup,
  BFormSelect,
  BButton,
  BTable,
} from "bootstrap-vue";
import { format, parseISO } from "date-fns";
import { mapActions } from "vuex";

function getLast12Months() {
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const today = new Date();
  let last12Months = [];

  for (let i = 0; i < 12; i++) {
    let date = new Date(today.getFullYear(), today.getMonth() - i, 1);
    let apiFormattedDate = `01 ${
      months[date.getMonth()]
    } ${date.getFullYear()}`; // "01 Jan 2024"
    let displayDate = `${months[date.getMonth()]} ${date.getFullYear()}`; // "Jan 2024"

    last12Months.push({
      value: apiFormattedDate,
      text: displayDate,
    });
  }
  return last12Months;
}

export default {
  components: {
    BContainer,
    BRow,
    BCol,
    BCard,
    BForm,
    BFormGroup,
    BFormSelect,
    BButton,
    BTable,
  },
  data() {
    return {
      fromMonth: null,
      toMonth: null,
      showTable: false,
      tableData: [],
      tableFields: [
        { key: "category", label: "Category" },
        { key: "period1", label: "Period 1 Amount" },
        { key: "period1Date", label: "Period 1 Date" },
        { key: "period2", label: "Period 2 Amount" },
        { key: "period2Date", label: "Period 2 Date" },
      ],
      last12Months: getLast12Months(),
    };
  },
  computed: {
    tdsHeader() {
      return this.fromMonth && this.toMonth
        ? `Payroll Variance Period1: ${this.fromMonth} Period 2: ${this.toMonth}`
        : "Payroll Variance Data";
    },
    formattedTableData() {
      return this.tableData.map((item) => ({
        category: item.category ?? "-",
        period1: item.period1 ?? "-",
        period1Date: item.period1Date
          ? format(parseISO(item.period1Date), "yyyy-MM-dd") // Convert to "YYYY-MM-DD"
          : "-",
        period2: item.period2 ?? "-",
        period2Date: item.period2Date
          ? format(parseISO(item.period2Date), "yyyy-MM-dd") // Convert to "YYYY-MM-DD"
          : "-",
        tenantId: item.tenantId ?? "-",
      }));
    },
  },
  methods: {
    ...mapActions("reportModule", ["getPayrollVarianceReportAction"]),

    async submitRange() {
      if (!this.fromMonth || !this.toMonth) {
        Swal.fire(
          "Error",
          "Please select both 'From' and 'To' months.",
          "error"
        );
        return;
      }

      Swal.fire({
        title: "Fetching Data...",
        text: "Please wait while the data is being loaded.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const period1 = this.fromMonth;
      const period2 = this.toMonth;

      if (!period1 || !period2) {
        Swal.fire(
          "Error",
          "Invalid period selection. Please try again.",
          "error"
        );
        return;
      }

      try {
        const payload = { dateFrom: period1, dateTo: period2 };
        console.log("API Request Payload:", payload);
        const response = await this.getPayrollVarianceReportAction(payload);
        Swal.close();
        console.log("API Response:", response);

        if (response && response.length > 0) {
          this.tableData = response;
          this.showTable = true;
        } else {
          Swal.fire(
            "No Data",
            "No data available for the selected period.",
            "warning"
          );
        }
      } catch (error) {
        console.error("Error fetching data:", error);

        if (
          error.response &&
          error.response.data &&
          error.response.data.errors
        ) {
          let errorMessage = "Validation errors occurred:\n";
          for (const key in error.response.data.errors) {
            errorMessage += `${key}: ${error.response.data.errors[key].join(
              ", "
            )}\n`;
          }
          Swal.fire("Validation Error", errorMessage, "error");
        } else {
          Swal.fire(
            "Error",
            "Failed to fetch data. Please try again.",
            "error"
          );
        }
      }
    },

    exportToCSV() {
      const headers = this.tableFields.map((field) => field.label).join(",");
      const rows = this.formattedTableData.map((item) =>
        this.tableFields.map((field) => `"${item[field.key] ?? "-"}"`).join(",")
      );
      const csvContent = [headers, ...rows].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `VARIANCE_Report_${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;
      link.click();
    },

    back() {
      this.showTable = false;
    },
  },
};
</script>
